import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { toColor, type Color } from "@salestrak/ui";
import {
  STAGE_TYPES,
  isClosedStageType,
  isStageType,
  type StageRow,
  type StageType,
} from "#app/lib/data";
import { stageTypeLabel } from "#app/lib/entity-visuals";
import { STAGE_TYPE_COLOR } from "#app/lib/stage-type";


// One drafted stage row. `id` null = created in this session; nothing touches
// the server until Apply. `key` is the row's stable identity across edits
// (the stage id for existing rows, a local counter for new ones).
export type StageDraft = {
  key: string;
  id: string | null;
  name: string;
  color: Color;
  type: StageType;
  isDefault: boolean;
  issueCount: number;
};

export type DraftGroups = Record<StageType, StageDraft[]>;

// A row with an unknown/legacy type seeds into the pending category so it stays VISIBLE —
// a row the dialog cannot show would be silently deleted by the full-list
// apply. The server adopts the drafted category for such rows.
function groupFor(type: string | null): StageType {
  return isStageType(type) ? type : "pending";
}

export function seedGroups(stages: StageRow[]): DraftGroups {
  const groups: DraftGroups = {
    pending: [],
    inProgress: [],
    done: [],
    canceled: [],
  };
  for (const s of [...stages].sort((a, b) => a.sortOrder - b.sortOrder)) {
    groups[groupFor(s.type)].push({
      key: s.stageId,
      id: s.stageId,
      name: s.name,
      color: toColor(s.icon?.color) ?? "dark-grey",
      type: groupFor(s.type),
      isDefault: s.isDefault,
      issueCount: s.issueCount,
    });
  }
  return groups;
}

// The dirty comparison ignores row identity churn it can't have (keys are
// stable) and compares exactly what Apply would send, plus order.
export function serializeGroups(groups: DraftGroups): string {
  return JSON.stringify(
    STAGE_TYPES.flatMap((t) =>
      groups[t].map((d) => [d.id, d.name.trim(), d.color, d.type, d.isDefault]),
    ),
  );
}

// Why a row can't be staged for deletion right now, or null when it can.
// The same floors the server enforces on Apply — surfaced at the affordance
// so the dialog can never draft an un-appliable list.
export function deleteBlocker(draft: StageDraft, groups: DraftGroups): string | null {
  if (draft.isDefault) {
    return "The default stage can't be deleted. Set another stage as default first.";
  }
  if (draft.issueCount > 0) {
    const label = draft.issueCount === 1 ? "1 Project" : `${draft.issueCount.toLocaleString()} Projects`;
    return `Move its ${label} to another stage first.`;
  }
  if (draft.type === "pending" && groups.pending.length <= 1) {
    return `Each workflow needs at least one ${stageTypeLabel("pending")} stage.`;
  }
  if (
    isClosedStageType(draft.type) &&
    groups.done.length + groups.canceled.length <= 1
  ) {
    return `Each workflow needs at least one ${stageTypeLabel("done")} or ${stageTypeLabel("canceled")} stage.`;
  }
  return null;
}

export type DraftActions = {
  onEdit: (key: string) => void;
  onExitEdit: () => void;
  onRename: (key: string, name: string) => void;
  onRecolor: (key: string, color: Color) => void;
  onSetDefault: (key: string) => void;
  onDelete: (key: string) => void;
};

/** The Apply payload: every drafted row in category order, trimmed. Blank
 *  names gate the Apply button, so every row here is sendable. */
export function toApplyInput(groups: DraftGroups) {
  return STAGE_TYPES.flatMap((type) => groups[type]).map((d) => ({
    id: d.id ?? undefined,
    name: d.name.trim(),
    color: d.color,
    type: d.type,
    isDefault: d.isDefault,
  }));
}

/**
 * The draft list over the fetched stages: seeded once the list resolves,
 * edited in place, compared against its baseline for dirtiness, and never
 * written to the server here — Apply is the caller's one commit.
 */
export function useStageDrafts({
  stages,
  loading,
  error,
}: {
  stages: StageRow[];
  loading: boolean;
  error: unknown;
}) {
  const [groups, setGroups] = useState<DraftGroups | null>(null);
  const baselineRef = useRef("");
  const draftCounterRef = useRef(0);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  // Seed the draft once the list resolves. The popup unmounts on close, so
  // every open starts from the live list; a background refetch mid-edit never
  // clobbers the draft (groups is already non-null).
  useEffect(() => {
    if (groups !== null) return;
    if (loading && stages.length === 0) return;
    if (error && stages.length === 0) return;
    const seeded = seedGroups(stages);
    baselineRef.current = serializeGroups(seeded);
    setGroups(seeded);
  }, [groups, loading, error, stages]);

  const dirty = groups !== null && serializeGroups(groups) !== baselineRef.current;

  const duplicateKeys = useMemo(() => {
    const dups = new Set<string>();
    if (!groups) return dups;
    const seen = new Set<string>();
    for (const type of STAGE_TYPES) {
      for (const row of groups[type]) {
        const key = row.name.trim().toLowerCase();
        if (!key) continue;
        if (seen.has(key)) dups.add(row.key);
        else seen.add(key);
      }
    }
    return dups;
  }, [groups]);

  const updateDraft = useCallback(
    (key: string, patch: Partial<Pick<StageDraft, "name" | "color">>) => {
      setGroups((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        for (const type of STAGE_TYPES) {
          next[type] = next[type].map((d) => (d.key === key ? { ...d, ...patch } : d));
        }
        return next;
      });
    },
    [],
  );

  const actions: DraftActions = useMemo(
    () => ({
      onEdit: (key) => setEditingKey(key),
      // Leaving edit keeps the row as typed — a blank name stays visible
      // with its error (Escape is the cancel gesture, handled in the row).
      onExitEdit: () => setEditingKey(null),
      onRename: (key, name) => updateDraft(key, { name }),
      onRecolor: (key, color) => updateDraft(key, { color }),
      onSetDefault: (key) => {
        setGroups((prev) => {
          if (!prev) return prev;
          const next = { ...prev };
          for (const type of STAGE_TYPES) {
            next[type] = next[type].map((d) => ({ ...d, isDefault: d.key === key }));
          }
          return next;
        });
      },
      onDelete: (key) => {
        setGroups((prev) => {
          if (!prev) return prev;
          const next = { ...prev };
          for (const type of STAGE_TYPES) {
            next[type] = next[type].filter((d) => d.key !== key);
          }
          return next;
        });
      },
    }),
    [updateDraft],
  );

  const addStage = useCallback((type: StageType) => {
    const key = `stage-draft-${++draftCounterRef.current}`;
    setGroups((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [type]: [
          ...prev[type],
          {
            key,
            id: null,
            name: "",
            color: STAGE_TYPE_COLOR[type],
            type,
            isDefault: false,
            issueCount: 0,
          },
        ],
      };
    });
    setEditingKey(key);
  }, []);

  const draftByKey = useMemo(() => {
    const map = new Map<string, StageDraft>();
    if (groups) {
      for (const type of STAGE_TYPES) for (const d of groups[type]) map.set(d.key, d);
    }
    return map;
  }, [groups]);

  // Only reorder within the same category — a cross-category move is a type
  // change, which the dialog deliberately does not do.
  const reorder = useCallback(
    (activeKey: string, overKey: string) => {
      const dragged = draftByKey.get(activeKey);
      const target = draftByKey.get(overKey);
      if (!dragged || !target) return;
      if (dragged.type !== target.type) return;
      setGroups((prev) => {
        if (!prev) return prev;
        const list = prev[dragged.type];
        const from = list.findIndex((d) => d.key === dragged.key);
        const to = list.findIndex((d) => d.key === target.key);
        if (from < 0 || to < 0) return prev;
        return { ...prev, [dragged.type]: arrayMove(list, from, to) };
      });
    },
    [draftByKey],
  );

  // Leaving edit from outside the row: a drag grabbing the grip keeps the
  // typed name and takes over.
  const exitEdit = useCallback(() => setEditingKey(null), []);

  const hasDuplicates = duplicateKeys.size > 0;
  const hasBlank =
    groups !== null &&
    STAGE_TYPES.some((type) => groups[type].some((d) => d.name.trim() === ""));

  return {
    groups,
    dirty,
    editingKey,
    exitEdit,
    duplicateKeys,
    hasDuplicates,
    hasBlank,
    actions,
    addStage,
    draftByKey,
    reorder,
  };
}
